import omit from 'lodash/omit';

import {
  LinkCategory,
  NodeCategory,
} from 'src/containers/ProjectDiagram/constants/categories';
import {
  BaseLinkData,
  BaseNodeData,
  ModelRule,
} from 'src/containers/ProjectDiagram/types/data';
import { DiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import {
  isModelConceptClass,
  isModelConceptHierarchy,
  isModelConceptRelationship,
} from 'src/containers/ProjectDiagram/utils/data';
import {
  ProjectModelClass,
  ProjectModelProperty,
  ProjectsState,
} from 'src/store/state/projects';
import { ReduxState } from 'src/store/types';
import { replaceInvalidIriChars } from 'src/utils/removeInvalidIriChars';

const getDefaultProjectPrefix = (project: Pick<Project, 'name'>) =>
  `tag:stardog:designer:${replaceInvalidIriChars(project.name)}:`;

const getDefaultProjectModelPrefix = (
  project: Pick<Project, 'name'>,
  modelName: string
) => `${getDefaultProjectPrefix(project)}${replaceInvalidIriChars(modelName)}:`;

// partial types
type OldProject = {
  id: string;
  name: string;
  dateLastUpdated: number;
  diagram: DiagramSnapshot;
  modelsById: Record<string, OldProjectModel>;
  modelNamespace: string;
};
type Project = {
  id: string;
  name: string;
  dateLastUpdated: number;
  defaultModel: ProjectModel;
  diagram: DiagramSnapshot;
  modelsById: Record<string, ProjectModel>;
};

type OldProjectModel = {
  connectionId: string | null;
  databaseId: string;
  databaseNamespaces: Record<string, string>;
  id: string;
  name: string;
  dateLastUpdated: number;
  classes: Record<string, ProjectModelClass> | null;
  properties: Record<string, ProjectModelProperty> | null;
  colorIdsByIri: Record<string, string> | null;
  descriptionsByIri: Record<string, string> | null;
  rules: Record<string, ModelRule> | null;
  labelsByIri: Record<string, string> | null;
  voicebox?: boolean;
  draft?: boolean;
};
type ProjectModel = {
  connectionId: string | null;
  databaseId: string | null;
  databaseNamespaces: Record<string, string> | null;
  id: string;
  name: string;
  dateLastUpdated: number;
  classes: Record<string, ProjectModelClass> | null;
  properties: Record<string, ProjectModelProperty> | null;
  colorIdsByIri: Record<string, string> | null;
  descriptionsByIri: Record<string, string> | null;
  rules: Record<string, ModelRule> | null;
  labelsByIri: Record<string, string> | null;
  namespace: string;
  voicebox?: boolean;
  draft?: boolean;
};

export interface OldModelConceptAttributeData {
  value: string;
  models: string[];
}

export interface OldModelConceptAttribute {
  value: string;
  domains: Record<string, OldModelConceptAttributeData>;
  ranges: Record<string, OldModelConceptAttributeData>;
  models: string[];
}

export interface OldModelConceptClass extends BaseNodeData {
  category: NodeCategory.MODEL_CONCEPT_CLASS;

  isAttributesOpen: boolean;
  models: string[];
}

export interface OldModelConceptRelationship extends BaseLinkData {
  category: LinkCategory.MODEL_CONCEPT_RELATIONSHIP;

  models: string[];
}

export interface OldModelConceptHierarchy extends BaseLinkData {
  category: LinkCategory.MODEL_CONCEPT_HIERARCHY;
  from: string;
  to: string;

  models: string[];
}

const DEFAULT_PROJECT_MODEL_ID = '__default__';

/**
 * * on {@link Project} add `defaultModel` field
 * * on {@link Project} move `modelNamespace` field to `defaultModel.namespace`
 * * on {@link ModelConceptAttributeData} remove `models` field
 * * on {@link ModelConceptAttribute} remove `models` field
 * * on {@link ModelConceptClass} remove `models` field
 * * on {@link ModelConceptRelationship} remove `models` field
 * * on {@link ModelConceptHierarchy} remove `models` field
 */
// prevState must be typed as `any` since it's an older version of the Redux State
export const v28 = (prevState: any): ReduxState => {
  const nextState = { ...prevState };

  if (nextState.projects) {
    nextState.projects = v28Projects(prevState.projects);
  }

  return nextState;
};

const v28Projects = (prevProjectsState: any): ProjectsState => {
  const projectsById: Record<string, Project> = {};

  Object.entries(
    prevProjectsState.projectsById as Record<string, OldProject>
  ).forEach(([projectId, project]) => {
    const { dateLastUpdated } = project;
    const defaultModel: ProjectModel = {
      classes: null,
      colorIdsByIri: null,
      connectionId: null,
      databaseId: null,
      databaseNamespaces: null,
      dateLastUpdated,
      descriptionsByIri: null,
      id: DEFAULT_PROJECT_MODEL_ID,
      labelsByIri: null,
      name: project.name,
      namespace:
        project.modelNamespace ||
        getDefaultProjectModelPrefix(project, 'model'),
      properties: null,
      rules: null,
    };

    const modelsByIri: ModelData['modelsByIri'] = {};
    const addProjectModelIdsToIri = (iri: string) => {
      modelsByIri[iri] = [DEFAULT_PROJECT_MODEL_ID];
    };

    const attributesByIri = Object.fromEntries(
      Object.entries(project.diagram.model.modelData.attributesByIri).map(
        ([key, data]) => {
          const oldAttribute = data as OldModelConceptAttribute;
          const domains = Object.fromEntries(
            Object.entries(oldAttribute.domains).map(([key, data]) => {
              addProjectModelIdsToIri(data.value);
              return [key, omit(data, 'models')];
            })
          );
          const ranges = Object.fromEntries(
            Object.entries(oldAttribute.ranges).map(([key, data]) => {
              addProjectModelIdsToIri(data.value);
              return [key, omit(data, 'models')];
            })
          );
          addProjectModelIdsToIri(oldAttribute.value);
          const attribute = omit(
            { ...oldAttribute, domains, ranges },
            'models'
          );
          return [key, attribute];
        }
      )
    );

    const nodeDataArray = project.diagram.model.nodeDataArray.map((data) => {
      if (isModelConceptClass(data)) {
        const conceptClass = data as unknown as OldModelConceptClass;
        addProjectModelIdsToIri(conceptClass.value);
        return omit(conceptClass, 'models');
      }
      return data;
    });
    const linkDataArray = project.diagram.model.linkDataArray.map((data) => {
      if (isModelConceptRelationship(data)) {
        const conceptRelationship =
          data as unknown as OldModelConceptRelationship;
        addProjectModelIdsToIri(conceptRelationship.value);
        return omit(conceptRelationship, 'models');
      }
      if (isModelConceptHierarchy(data)) {
        const conceptHierarchy = data as unknown as OldModelConceptHierarchy;
        addProjectModelIdsToIri(conceptHierarchy.value);
        return omit(conceptHierarchy, 'models');
      }
      return data;
    });

    const modelsById = Object.fromEntries(
      Object.entries(project.modelsById).map(([id, { ...oldModel }]) => {
        const model: ProjectModel = {
          ...oldModel,
          namespace: getDefaultProjectModelPrefix(project, oldModel.name),
        };
        return [id, model];
      })
    );

    projectsById[projectId] = {
      ...project,
      defaultModel,
      diagram: {
        ...project.diagram,
        model: {
          ...project.diagram.model,
          modelData: {
            ...project.diagram.model.modelData,
            attributesByIri,
            modelsByIri,
          },
          nodeDataArray,
          linkDataArray,
        },
      },
      modelsById,
    };
  });

  return { ...prevProjectsState, projectsById };
};
